import React from "react";
import { useNavigate } from "react-router";
import { ChevronLeft } from "lucide-react";
import { cn } from "./ui/utils";
import { SAFE_TOP_PAD } from "../utils/safeArea";

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  back?: boolean;
  /** Fallback when there is no in-app history (e.g. opened from tab bar). */
  backTo?: string;
  onBack?: () => void;
  right?: React.ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  back,
  backTo = "/home",
  onBack,
  right,
  className,
}: PageHeaderProps) {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    const idx = (window.history.state as { idx?: number } | null)?.idx;
    if (typeof idx === "number" && idx > 0) {
      navigate(-1);
      return;
    }
    navigate(backTo);
  };

  return (
    <div
      className={cn(
        "sticky top-0 z-40 bg-liquid-glass-panel border-b border-white/[0.08]",
        SAFE_TOP_PAD,
        className
      )}
    >
      <div className="flex items-center h-12 px-4 gap-3">
        {back && (
          <button
            type="button"
            onClick={handleBack}
            aria-label="Back"
            className="flex items-center justify-center w-8 h-8 -ml-1 rounded-full active:bg-black/5 transition-colors"
          >
            <ChevronLeft size={22} className="text-gray-700" />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <h1 className="text-[17px] truncate text-gray-900">{title}</h1>
          {subtitle && (
            <p className="text-[12px] text-gray-500 truncate -mt-0.5">{subtitle}</p>
          )}
        </div>
        {right && <div className="flex-shrink-0">{right}</div>}
      </div>
    </div>
  );
}
